/**
 * The letters of representation, as the editor sees them (Lloyd, Aug 17 2026).
 *
 * Everything here mirrors backend/src/utils/letters/letterContent.ts. The server validates on its
 * own account — these types are for the screen, not a substitute for that.
 */

export type LetterKey = 'not-guilty' | 'dsc-under-25' | 'deferral';
export type TemplateKey = LetterKey | 'attorneys' | 'court-email';

export type LetterCondition =
  | 'always'
  | 'court-date-known'
  | 'dsc-taken'
  | 'dsc-not-taken'
  | 'ticket-number-known'
  | 'ticket-number-unknown'
  | 'has-not-guilty-attachments';

export interface LetterListItem {
  text: string;
  subItems?: string[];
}

export interface LetterBlock {
  /** 'details' is the email's ticket-details box — a block so its position is editable too. */
  type: 'paragraph' | 'list' | 'details';
  showWhen?: LetterCondition;
  text?: string;
  leadInText?: string;
  leadInWhen?: LetterCondition;
  items?: LetterListItem[];
  ordered?: boolean;
  variant?: 'closing';
}

export interface LetterContent {
  blocks: LetterBlock[];
}

export interface LetterAttorney {
  name: string;
  barNumber: string;
  title: string;
}

export interface AttorneysContent {
  attorneys: LetterAttorney[];
}

export interface CourtEmailContent {
  subject: string;
  heading: string;
  blocks: LetterBlock[];
  signOff: string;
  signOffName: string;
}

export interface LetterValidationIssue {
  where: string;
  message: string;
}

export interface TemplateVersionSummary {
  version: number;
  publishedAt: string;
  publishedBy: string | null;
  note: string | null;
  revertedFromVersion: number | null;
}

export interface TemplateResponse<T> {
  key: TemplateKey;
  label: string;
  content: T;
  isDefault: boolean;
  version: number | null;
  publishedAt: string | null;
  publishedBy: string | null;
  versions: TemplateVersionSummary[];
  canEdit: boolean;
}

export interface LettersMeta {
  letters: { key: LetterKey; label: string }[];
  placeholders: { name: string; label: string }[];
  conditions: { value: LetterCondition; label: string }[];
  /** The email is filled from different data and varies on different facts, so it has its own. */
  emailPlaceholders: { name: string; label: string }[];
  emailConditions: { value: LetterCondition; label: string }[];
  canEdit: boolean;
}

const BASE = (process.env.API_HOST || '') + '/api/v1/letters';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(BASE + path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.message || 'Request failed') as Error & {
      issues?: LetterValidationIssue[];
    };
    error.issues = payload?.issues;
    throw error;
  }
  return payload as T;
}

export const LettersApi = {
  meta: () => request<LettersMeta>('/meta'),

  getLetter: (key: LetterKey) =>
    request<TemplateResponse<LetterContent>>(`/template/${key}`),

  getAttorneys: () =>
    request<TemplateResponse<AttorneysContent>>('/template/attorneys'),

  getCourtEmail: () =>
    request<TemplateResponse<CourtEmailContent>>('/template/court-email'),

  getVersion: (key: TemplateKey, version: number) =>
    request<{ key: TemplateKey; version: number; content: unknown }>(
      `/template/${key}/version/${version}`,
    ),

  validate: (key: TemplateKey, content: unknown) =>
    request<{ issues: LetterValidationIssue[] }>(`/template/${key}/validate`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),

  preview: (
    key: LetterKey,
    content: LetterContent,
    options: {
      attorneys?: AttorneysContent | null;
      courtDateKnown?: boolean;
      hasTakenDscInPastYear?: boolean;
    } = {},
  ) =>
    request<{ pdfBase64: string }>(`/template/${key}/preview`, {
      method: 'POST',
      body: JSON.stringify({ content, ...options }),
    }),

  previewEmail: (
    content: CourtEmailContent,
    options: { ticketNumberKnown?: boolean; hasNotGuiltyAttachments?: boolean } = {},
  ) =>
    request<{ html: string }>('/template/court-email/preview', {
      method: 'POST',
      body: JSON.stringify({ content, ...options }),
    }),

  publish: (key: TemplateKey, content: unknown, note: string | null) =>
    request<{ version: number; versions: TemplateVersionSummary[] }>(
      `/template/${key}/publish`,
      { method: 'POST', body: JSON.stringify({ content, note }) },
    ),

  revert: (key: TemplateKey, version: number) =>
    request<{ version: number; versions: TemplateVersionSummary[] }>(
      `/template/${key}/revert/${version}`,
      { method: 'POST' },
    ),
};
